import { ArrowBackIos, Visibility, VisibilityOff, Warning } from '@mui/icons-material'
import { Box, Button, FormControl, FormHelperText, Grid, IconButton, Input, InputAdornment, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation, useParams } from 'react-router'
import logo from '../../../../assets/Images/logos/logodark.svg'
import { rememberPassword, resetPassword } from '../../../../redux/actions/auth'
import { formValidator, resetFormValidator } from '../../../../redux/actions/validator'
import { IRootState } from '../../../../redux/types'
import CustomSnackBar from '../../../shared/components/CustomSnackBar'
import ScreenLoading from '../../../shared/components/ScreenLoading'
import { Container, FormBody, GridForm, StyledInput } from '../../../shared/styles/LoginRegisterStyle'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

function RememberPassword() {
  const { t } = useTranslation(['global'])

  let query = useQuery()
  let { token } = useParams()
  let emailQuery = query.get('email')

  const dispatch = useDispatch()
  const { isValidForm, errorForm, msgForm } = useSelector((state: IRootState) => state.validator)
  const { isAuth, loading, msg, error } = useSelector((state: IRootState) => state.auth)

  const [user, setUser] = useState({
    email: '',
    verificationCode: '',
    password: '',
    confirmPassword: '',
    showPassword: false,
    showForm: false
  })

  useEffect(() => {
    if (token != undefined && emailQuery != null) {
      setUser({ ...user, showForm: true, email: emailQuery, verificationCode: token })
    }
  }, [])

  useEffect(() => {
    if (isValidForm) {
      dispatch(resetFormValidator())

      if (user.password !== '') dispatch(resetPassword({ user }))
      else dispatch(rememberPassword({ email: user.email, sentFrom: 'admin' }))
    }
  }, [isValidForm])

  const handleClickShowPassword = () => {
    setUser({ ...user, showPassword: !user.showPassword })
  }

  const handleChange = (inputName: string) => (event: any) => {
    setUser({ ...user, [inputName]: event.target.value })
  }

  const handleSubmit = () => {
    dispatch(
      formValidator({
        email: [user.email, 'required|max:45|min:3']
      })
    )
  }

  const handleSubmitPassword = () => {
    dispatch(
      formValidator({
        email: [user.email, 'required|max:45|min:3'],
        verificationCode: [user.verificationCode, 'required|max:10|min:3'],
        password: [user.password, 'required|max:45|min:3'],
        confirmPassword: [user.confirmPassword, `required|max:45|min:3|equal:${user.password}`]
      })
    )
  }

  if (isAuth) {
    return <Navigate to="/inicio" replace />
  }

  return (
    <>
      <Container container>
        <GridForm>
          <FormBody container spacing={3}>
            <Grid item xs={12} style={{ marginBottom: 8 }}>
              <Button variant="text" size="small" color="primary" startIcon={<ArrowBackIos />} href="/">
                {t('back')}
              </Button>
            </Grid>
            <Grid item xs={12} style={{ marginBottom: 40 }}>
              <img src={logo} height="36" alt="logo" />
            </Grid>
            <Grid item xs={12}>
              <Typography color="textSecondary">{t('login.remember-password-text')}</Typography>
            </Grid>

            <Grid item xs={12}>
              <form noValidate autoComplete="off">
                <Box mb={0.5}>
                  <Typography>{t('email')}</Typography>
                </Box>
                <StyledInput
                  id="email-login"
                  value={user.email}
                  placeholder={t('email')}
                  onChange={handleChange('email')}
                  fullWidth
                  required
                  disabled={user.showForm}
                  error={errorForm.email}
                />
                <br />
                {msgForm.email && (
                  <FormHelperText error>
                    <Warning fontSize="small" style={{ marginRight: '10px' }} /> {msgForm.email}
                  </FormHelperText>
                )}

                {user.showForm ? (
                  <Box display="flex" flexDirection="column" width="100%" gap={16} pt={2}>
                    <FormControl style={{ display: 'none' }} variant="filled" fullWidth>
                      <Box mb={0.5}>
                        <Typography>{t('login.verification-code')}</Typography>
                      </Box>
                      <Input
                        id="verificationCode"
                        placeholder={t('login.verification-code')}
                        value={token}
                        onChange={handleChange('verificationCode')}
                        fullWidth
                        required
                        error={errorForm.verificationCode}
                      />
                    </FormControl>

                    <FormControl variant="filled" fullWidth>
                      <Box mb={0.5}>
                        <Typography>{t('login.new-password')}</Typography>
                      </Box>
                      <Input
                        id="password1"
                        required
                        placeholder={t('login.new-password')}
                        type={user.showPassword ? 'text' : 'password'}
                        value={user.password}
                        error={errorForm.password}
                        onChange={handleChange('password')}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={event => event.preventDefault()}
                              edge="end"
                            >
                              {user.showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      <FormHelperText error id="outlined-helper-text">
                        {msgForm.password}
                      </FormHelperText>
                    </FormControl>

                    <FormControl variant="filled" fullWidth>
                      <Box mb={0.5}>
                        <Typography>{t('login.confirm-password')}</Typography>
                      </Box>
                      <Input
                        id="password2"
                        required
                        placeholder={t('login.confirm-password')}
                        type={user.showPassword ? 'text' : 'password'}
                        value={user.confirmPassword}
                        error={errorForm.confirmPassword}
                        onChange={handleChange('confirmPassword')}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={event => event.preventDefault()}
                              edge="end"
                            >
                              {user.showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      <FormHelperText error id="outlined-helper-text">
                        {msgForm.confirmPassword}
                      </FormHelperText>
                    </FormControl>

                    <Box mt={4}>
                      <Button variant="contained" fullWidth onClick={() => handleSubmitPassword()}>
                        {t('login.change-password')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box mt={4}>
                    <Button variant="contained" fullWidth onClick={() => handleSubmit()}>
                      {t('login.change-password')}
                    </Button>
                  </Box>
                )}
              </form>
            </Grid>
          </FormBody>
        </GridForm>
      </Container>
      <CustomSnackBar errors={[error]} msgs={[msg]} />
      <ScreenLoading loadings={[loading]} />
    </>
  )
}

export default RememberPassword
